import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check, MessageCircle, Phone, ArrowDown, Heart } from "lucide-react";
import { useLanguage, waLink, PHONE_DISPLAY, PHONE_TEL } from "../i18n";
import { EstimatorButton } from "./QuoteEstimator";

const HERO_IMG = "/images/puerta.jpg";
const COAST_IMG =
  "https://images.pexels.com/photos/13658485/pexels-photo-13658485.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const EASE = [0.22, 1, 0.36, 1];

const Hero = () => {
  const { t, lang } = useLanguage();
  const firstRender = useRef(true);
  useEffect(() => {
    firstRender.current = false;
  }, []);
  const base = firstRender.current ? 1.9 : 0;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      id="inicio"
      ref={ref}
      className="relative overflow-hidden pt-36 pb-16 sm:pt-44 lg:pb-24"
      data-testid="hero-section"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 sm:px-6 lg:grid-cols-12 lg:gap-10 lg:px-8">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: base, ease: EASE }}
            className="mb-6 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-[#C85A32]"
            data-testid="hero-eyebrow"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#C85A32]" />
            {t.hero.eyebrow}
          </motion.p>

          <h1
            key={lang}
            data-testid="hero-heading"
            className="font-serif text-[#0D1B2A] tracking-tight leading-[1.02]"
          >
            <span className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.95, delay: base + 0.08, ease: EASE }}
                className="block text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black"
              >
                {t.hero.line1}
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-3">
              <motion.span
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.95, delay: base + 0.22, ease: EASE }}
                className="block font-script text-6xl sm:text-7xl lg:text-8xl xl:text-9xl text-[#1B6B93]"
              >
                {t.hero.line2}
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: base + 0.42, ease: EASE }}
            className="mt-6 max-w-xl text-base leading-relaxed text-[#0D1B2A]/70 sm:text-lg"
            data-testid="hero-subheading"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: base + 0.55, ease: EASE }}
            className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2"
            data-testid="hero-badges"
          >
            {t.hero.badges.map((b) => (
              <span
                key={b}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0D1B2A]"
              >
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#1B6B93] text-white">
                  <Check size={12} strokeWidth={3} />
                </span>
                {b}
              </span>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: base + 0.68, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              data-testid="hero-whatsapp-cta"
              href={waLink(t.waGeneric)}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2.5 rounded-full bg-[#25D366] px-7 py-4 text-base font-bold text-white shadow-xl shadow-[#25D366]/30 transition-all duration-300 hover:scale-[1.04] hover:shadow-2xl hover:shadow-[#25D366]/40"
            >
              <MessageCircle size={20} strokeWidth={2.4} className="transition-transform duration-300 group-hover:-rotate-12" />
              {t.hero.ctaPrimary}
            </a>
            <a
              data-testid="hero-call-cta"
              href={PHONE_TEL}
              className="inline-flex items-center gap-2.5 rounded-full border-2 border-[#0D1B2A] px-7 py-[14px] text-base font-bold text-[#0D1B2A] transition-colors duration-300 hover:bg-[#0D1B2A] hover:text-[#FAF6F0]"
            >
              <Phone size={18} strokeWidth={2.4} />
              {t.hero.ctaSecondary} · {PHONE_DISPLAY}
            </a>
            <EstimatorButton
              testId="hero-estimator-button"
              className="inline-flex items-center gap-2 rounded-full px-2 py-3 text-base font-bold text-[#1B6B93] underline decoration-[#1B6B93]/30 decoration-2 underline-offset-4 transition-colors duration-300 hover:text-[#C85A32] hover:decoration-[#C85A32]/50"
            />
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: base + 0.85 }}
            className="mt-8 font-script text-2xl text-[#C85A32] -rotate-2"
            data-testid="hero-script-note"
          >
            {t.hero.note}
          </motion.p>
        </motion.div>

        <motion.div style={{ y: yImg }} className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.1, delay: base + 0.3, ease: EASE }}
            className="relative overflow-hidden rounded-b-[2rem] rounded-t-[999px] border-[10px] border-white shadow-2xl shadow-[#0D1B2A]/20"
          >
            <img
              src={HERO_IMG}
              alt="Manitas trabajando en un hogar de la Marina Baixa"
              className="h-[420px] w-full object-cover sm:h-[540px]"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0D1B2A]/25 via-transparent to-transparent" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, rotate: -14, y: 30 }}
            animate={{ opacity: 1, rotate: -7, y: 0 }}
            transition={{ duration: 0.9, delay: base + 0.6, ease: EASE }}
            className="absolute -left-4 bottom-10 w-36 bg-white p-2 pb-3 shadow-xl shadow-[#0D1B2A]/25 sm:-left-12 sm:w-44"
          >
            <img
              src={COAST_IMG}
              alt="Costa de la Marina Baixa"
              className="h-24 w-full object-cover sm:h-28"
            />
            <p className="mt-2 text-center font-script text-base leading-tight text-[#0D1B2A] sm:text-lg">
              {t.hero.polaroid}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: base + 0.8, ease: EASE }}
            className="absolute -right-2 top-6 flex h-28 w-28 items-center justify-center rounded-full bg-[#C85A32] text-center shadow-xl shadow-[#C85A32]/40 sm:-right-6 sm:h-32 sm:w-32"
            data-testid="hero-guarantee-badge"
          >
            <p className="px-3 font-script text-lg leading-tight text-white sm:text-xl">
              {t.hero.stamp}
              <Heart size={14} className="mx-auto mt-1 fill-white" />
            </p>
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: base + 1.3, duration: 1 }}
        className="mt-16 flex justify-center"
      >
        <span className="inline-flex flex-col items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.3em] text-[#0D1B2A]/50">
          {t.hero.scroll}
          <motion.span
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          >
            <ArrowDown size={14} />
          </motion.span>
        </span>
      </motion.div>
    </section>
  );
};

export default Hero;
